import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import { SiteFooter } from "../components/SiteFooter";
import { homeSection } from '../lib/marketingHome';

const API = process.env.REACT_APP_BACKEND_URL;
const C = { bg: "#FAF7F0", ink: "#2A1E5C", ink2: "#5D5A6B", muted: "#8B7E6A", gold: "#F5CB5C", red: "#DE3C4B", line: "#E8E2D5", card: "#FFFFFF" };

const WORK_TYPE = [
  { v: "freelance", l: "Freelancer / creative" },
  { v: "1099", l: "1099 contractor" },
  { v: "gig", l: "Gig / platform work" },
  { v: "small_biz", l: "Solo small business owner" },
  { v: "mixed", l: "A mix of the above" },
];

const PRODUCT_INTEREST = [
  { v: "income_map", l: "Relay Calculator, figure out my weekly number" },
  { v: "payday_relay", l: "Payday Relay, the full 52-week system" },
  { v: "caddy", l: "The Payday Relay app, daily logging on my phone" },
  { v: "pro", l: "The Pit Crew, coaching Q&As" },
  { v: "not_sure", l: "Not sure yet, show me around" },
];

export default function BetaSurveyPage() {
  const [params] = useSearchParams();
  const ref = (params.get("ref") || "").trim().toUpperCase();

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [workType, setWorkType] = useState("");
  const [productInterest, setProductInterest] = useState("");
  const [tracking, setTracking] = useState(0);
  const [ui, setUi] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!ref) { setNotFound(true); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/beta-waitlist/survey?ref=${encodeURIComponent(ref)}`);
        if (cancelled) return;
        if (res.status === 404) { setNotFound(true); return; }
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || "Couldn't load");
        setInfo(data);
        if (data.already_submitted) setDone(true);
      } catch (e) {
        if (!cancelled) toast.error(e.message || "Couldn't load your survey");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [ref]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/beta-waitlist/survey`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tracking_code: ref,
          work_type: workType || null,
          product_interest: productInterest || null,
          tracking_experience: tracking || null,
          ui_rating: ui || null,
          feedback: feedback.trim() || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data?.detail?.[0]?.msg || data?.detail || "Couldn't save your answers");
        return;
      }
      setDone(true);
      toast.success("Thanks. Your notes are in.");
    } catch {
      toast.error("Network hiccup, try again in a sec");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: C.bg }}>
        <div className="text-center">
          <p className="text-sm" style={{ color: C.ink2 }}>Loading your survey…</p>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: C.bg }}>
        <div className="max-w-md text-center" data-testid="beta-survey-notfound">
          <h1 className="font-heading text-2xl mb-3" style={{ color: C.ink }}>We couldn't find that survey link</h1>
          <p className="text-sm mb-6" style={{ color: C.ink2 }}>
            The link in your confirmation email is unique, double-check you copied the whole thing, or join the beta again to get a fresh link.
          </p>
          <Link to={homeSection('beta')} className="inline-block px-6 py-3 rounded-full font-bold text-sm" style={{ background: C.gold, color: C.ink }} data-testid="beta-survey-back">
            Back to the beta signup →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: C.bg }}>
      <header className="py-3 px-4 sm:px-6 border-b" style={{ borderColor: C.line, background: C.card }}>
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm hover:opacity-70" style={{ color: C.ink }} data-testid="beta-survey-home-link">
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
          <img src="/images/paydayrelaylogo.png" alt="Payday Relay" className="h-16 w-auto" />
        </div>
      </header>

      <main className="px-4 sm:px-6 py-10">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <span className="inline-block px-3 py-1 rounded-full text-[11px] font-bold tracking-widest mb-4" style={{ background: C.red, color: "#FFFFFF" }}>
              🧪 BETA SURVEY
            </span>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-3" style={{ color: C.ink }} data-testid="beta-survey-title">
              {done ? "Thank you 🏁" : "Two quick minutes, help shape what we build"}
            </h1>
            <p className="font-body text-base leading-relaxed" style={{ color: C.ink2 }}>
              {done
                ? "Your notes are in. This is what actually shapes what we ship next, thank you for taking the time."
                : `You're signed up as ${info?.email || "a beta tester"}. Skip anything that doesn't fit.`}
            </p>
          </div>

          {done ? (
            <div className="text-center rounded-2xl p-8" style={{ background: C.card, border: `1px solid ${C.line}` }} data-testid="beta-survey-thanks">
              <p className="font-body text-base mb-6" style={{ color: C.ink2 }}>
                Your promo code from the confirmation email still works, check your inbox if you need it again.
              </p>
              <Link to="/" className="inline-block px-6 py-3 rounded-full font-bold text-sm" style={{ background: C.gold, color: C.ink }} data-testid="beta-survey-done-cta">
                Head back to the site →
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-6" data-testid="beta-survey-form">
              <FieldSelect
                label="What does your work look like?"
                testId="survey-work-type"
                value={workType}
                onChange={setWorkType}
                options={WORK_TYPE}
              />
              <FieldSelect
                label="What are you most curious about?"
                testId="survey-product-interest"
                value={productInterest}
                onChange={setProductInterest}
                options={PRODUCT_INTEREST}
              />
              <RatingRow
                label="How well are you tracking your income today?"
                hintLow="Total chaos"
                hintHigh="Rock solid"
                value={tracking}
                onChange={setTracking}
                testId="survey-tracking-rating"
              />
              <RatingRow
                label="First impression of how the site looks & feels"
                hintLow="Rough"
                hintHigh="Love it"
                value={ui}
                onChange={setUi}
                testId="survey-ui-rating"
              />
              <div>
                <label className="block font-body text-sm font-bold mb-1.5" style={{ color: C.ink }} htmlFor="survey-feedback">
                  Notes, questions, or feedback <span className="font-normal" style={{ color: C.muted }}>(anything you want us to know)</span>
                </label>
                <textarea
                  id="survey-feedback"
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  rows={5}
                  maxLength={2000}
                  placeholder="What's confusing? What would you love to see? Any features missing?"
                  className="w-full px-4 py-3 rounded-xl border text-base"
                  style={{ borderColor: C.line, background: C.card, color: C.ink }}
                  data-testid="survey-feedback"
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                className="w-full py-4 rounded-full font-heading font-bold text-base transition-transform hover:scale-[1.02] disabled:opacity-50 inline-flex items-center justify-center gap-2"
                style={{ background: C.gold, color: C.ink }}
                data-testid="survey-submit"
              >
                {busy ? "Saving your answers…" : (<><Check className="w-4 h-4" /> Send my answers</>)}
              </button>
              <p className="font-body text-xs text-center" style={{ color: C.muted }}>
                Reference {ref} · answers save straight to your beta record
              </p>
            </form>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

const FieldSelect = ({ label, testId, value, onChange, options }) => (
  <div>
    <label className="block font-body text-sm font-bold mb-1.5" style={{ color: C.ink }}>{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-4 py-3 rounded-xl border text-base"
      style={{ borderColor: C.line, background: C.card, color: C.ink }}
      data-testid={testId}
    >
      <option value="">, Pick one, </option>
      {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  </div>
);

const RatingRow = ({ label, hintLow, hintHigh, value, onChange, testId }) => (
  <div>
    <label className="block font-body text-sm font-bold mb-1.5" style={{ color: C.ink }}>{label}</label>
    <div className="flex items-center gap-2" data-testid={testId}>
      {[1, 2, 3, 4, 5].map((n) => {
        const active = value >= n;
        return (
          <button
            key={n}
            type="button"
            onClick={() => onChange(value === n ? 0 : n)}
            className="w-11 h-11 rounded-full font-bold text-sm transition-transform hover:scale-105"
            style={{
              background: active ? C.gold : C.card,
              border: `2px solid ${active ? C.gold : C.line}`,
              color: C.ink,
            }}
            data-testid={`${testId}-${n}`}
            aria-label={`Rate ${n} of 5`}
          >
            {n}
          </button>
        );
      })}
    </div>
    <div className="flex justify-between mt-1.5 font-body text-xs" style={{ color: C.muted }}>
      <span>{hintLow}</span><span>{hintHigh}</span>
    </div>
  </div>
);
